// Enums
export enum UserRole {
  ADMIN = 'ADMIN',
  GESTOR = 'GESTOR',
  OPERADOR = 'OPERADOR',
  OFICINA = 'OFICINA',
}

export enum TituloStatus {
  ABERTO = 'ABERTO',
  FECHADO = 'FECHADO',
  PARCIAL = 'PARCIAL',
  VENCIDO = 'VENCIDO',
}

export enum StatusNegociacao {
  PENDENTE = 'PENDENTE',
  APROVADA = 'APROVADA',
  REJEITADA = 'REJEITADA',
  CANCELADA = 'CANCELADA',
  PAGA = 'PAGA',
  PARCIAL = 'PARCIAL',
}

export enum StatusPagamento {
  PENDENTE = 'PENDENTE',
  CONFIRMADO = 'CONFIRMADO',
  PARCIAL = 'PARCIAL',
  CANCELADO = 'CANCELADO',
  ESTORNADO = 'ESTORNADO',
}

// User types
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  gestorLeadsUserId?: number | null;
  allowedModules?: string[] | null;
  /** URL relativa (ex. /uploads/...) ou absoluta; exibida no portal se existir. */
  avatarUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  allowedModules?: string[] | null;
  avatarUrl?: string | null;
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

// Company types
export interface Company {
  id: string;
  name: string;
  document: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Push notification types
export const PUSH_TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  general: 'Geral',
  cobranca: 'Cobrança',
  pagamento: 'Pagamento',
  lembrete: 'Lembrete',
  alerta: 'Alerta',
  informacao: 'Informação',
  promocao: 'Promoção',
};

export interface PushTemplate {
  id: string;
  name: string;
  title: string;
  body: string;
  variables: string[];
  category: string;
  companyId: string | null;
  createdBy: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user?: { name: string; email: string };
  company?: { id: string; name: string };
}

export interface SendPushRequest {
  templateId?: string;
  title?: string;
  body?: string;
  /** Enviar para todos os usuários ativos do app (ZehMotoca). */
  sendToAll?: boolean;
  clienteFornecedorIds?: string[];
  companyIds?: string[];
  data?: Record<string, unknown>;
}

export interface SendPushResult {
  successCount: number;
  failedCount: number;
  totalRecipients: number;
  totalTokens: number;
}

// Titulo types
export interface Titulo {
  id: string;
  companyId: string;
  numero: string;
  valor: number;
  vencimento: string;
  status: TituloStatus;
  observacoes?: string;
  urlBoleto?: string;
  email?: string;
  operadorId?: string;
  dataAtribuicao?: string;
  dataUltimaAcao?: string;
  tentativasContato: number;
  statusLocacao?: string;
  createdAt: string;
  updatedAt: string;
  company: Company;
  operador?: Pick<User, 'id' | 'name' | 'email'>;
  _count?: {
    feedbacks: number;
    lembretes: number;
  };
  feedbacks?: Feedback[];
  lembretes?: Lembrete[];
  historico?: HistoricoTitulo[];
}

export interface CreateTituloRequest {
  companyId: string;
  numero: string;
  valor: number;
  vencimento: string;
  status: TituloStatus;
  observacoes?: string;
  urlBoleto?: string;
  email?: string;
}

export interface UpdateTituloRequest {
  valor?: number;
  vencimento?: string;
  status?: TituloStatus;
  observacoes?: string;
  operadorId?: string;
  urlBoleto?: string;
  email?: string;
}

// Comprovante types
export interface ComprovantePagamento {
  id: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  createdAt: string;
}

// Feedback types
export interface Feedback {
  id: string;
  tituloId: string;
  userId: string;
  tipo: string;
  descricao: string;
  tipoNegociacao?: string;
  valorNegociado?: string | null;
  proximoContato?: string | null;
  titulosNegociados?: string[] | null;
  titulosNegociadosData?: Titulo[]; // Dados completos dos títulos negociados
  valorTotalNegociado?: number | null;
  dataVencimentoNegociacao?: string | null;
  statusNegociacao?: StatusNegociacao;
  observacoesPagamento?: string | null;
  // Campos para Gestor de Cobranças
  acaoGestor?: boolean;
  acaoGestorConcluida?: boolean;
  observacoesGestor?: string | null;
  dataAcaoGestor?: string | null;
  createdAt: string;
  user: Pick<User, 'id' | 'name'>;
  clienteFornecedorId?: string | null;
  // Dados de pagamento e anexos
  historicoPagamentos?: HistoricoPagamento[];
  comprovantes?: ComprovantePagamento[];
}

export interface CreateFeedbackRequest {
  tituloId: string;
  tipo: string;
  descricao: string;
  tipoNegociacao?: string;
  valorNegociado?: string;
  proximoContato?: string;
  titulosNegociados?: string[];
  valorTotalNegociado?: number;
  dataVencimentoNegociacao?: string;
  statusNegociacao?: StatusNegociacao;
  observacoesPagamento?: string;
}

export interface HistoricoPagamento {
  id: string;
  feedbackId: string;
  tituloId: string;
  valorNegociado: number;
  valorPago?: number | null;
  dataNegociacao: string;
  dataPagamento?: string | null;
  statusPagamento: StatusPagamento;
  formaPagamento?: string | null;
  observacoes?: string | null;
  comprovanteId?: string | null;
  createdAt: string;
  updatedAt: string;
  comprovante?: ComprovantePagamento | null;
}

// Lembrete types
export interface Lembrete {
  id: string;
  tituloId: string;
  userId: string;
  titulo: string;
  descricao: string;
  dataLembrete: string;
  executado: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLembreteRequest {
  tituloId: string;
  titulo: string;
  descricao: string;
  dataLembrete: string;
}

// Historico types
export interface HistoricoTitulo {
  id: string;
  tituloId: string;
  statusAnterior?: TituloStatus;
  statusNovo: TituloStatus;
  observacoes?: string;
  userId?: string;
  createdAt: string;
}

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Filter and pagination types
export interface FilterQuery {
  search?: string;
  status?: TituloStatus;
  operadorId?: string;
  companyId?: string;
  vencimentoInicio?: string;
  vencimentoFim?: string;
  diasAtraso?: string;
  kanbanId?: string;
}

export interface PaginationQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Kanban types
export interface KanbanColumn {
  id: TituloStatus;
  title: string;
  titulos: Titulo[];
}

export interface KanbanData {
  [TituloStatus.ABERTO]: Titulo[];
  [TituloStatus.PARCIAL]: Titulo[];
  [TituloStatus.VENCIDO]: Titulo[];
  [TituloStatus.FECHADO]: Titulo[];
}

// Statistics types
export interface Estatisticas {
  totalTitulos: number;
  titulosAbertos: number;
  titulosFechados: number;
  titulosVencidos: number;
  valorTotal: number;
  valorRecuperado: number;
  taxaRecuperacao: number;
}

// Distribution types
export interface DistribuicaoOperador {
  operadorId: string;
  nome: string;
  titulosAtivos: number;
  ultimaAtribuicao?: string;
}

// Import types
export interface ImportResult {
  totalProcessados: number;
  sucessos: number;
  erros: number;
  detalhesErros: string[];
  tempoProcessamento: number;
}

// External API Import types
export interface ExternalImportResult {
  success: boolean;
  totalReceived: number;
  totalProcessed: number;
  totalCreated: number;
  totalUpdated: number;
  errors: string[];
  periodo: {
    inicio: string;
    fim: string;
  };
  titulos: Array<{
    externalId: string;
    internalId?: string;
    status: 'created' | 'updated' | 'error';
    error?: string;
  }>;
}

export interface ExternalApiConfig {
  enabled: boolean;
  hasUrl: boolean;
  hasToken: boolean;
  hasCnpj: boolean;
  defaultPeriod: {
    data_inicio: string;
    data_fim: string;
  };
}

// Configuração completa da API externa
export interface ExternalApiFullConfig {
  id?: string;
  enabled: boolean;
  url: string;
  token: string;
  cnpj: string;
  timeout: number;
  defaultPeriodDays: number;
  dataInicio?: string;
  dataFim?: string;
  autoSyncEnabled: boolean;
  syncInterval: string;
  lastSyncAt?: Date;
  lastSyncStatus?: 'success' | 'error';
  lastSyncError?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// Job de sincronização
export interface ExternalApiJob {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  cronExpression: string;
  lastRun?: Date;
  nextRun?: Date;
  status: 'active' | 'inactive' | 'error';
  lastResult?: {
    success: boolean;
    message: string;
    executedAt: Date;
    duration: number;
    dataProcessed?: number;
    errors?: string[];
  };
  createdAt: Date;
  updatedAt: Date;
}

// Resultado de teste de conexão
export interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: {
    total: number;
    periodo: {
      inicio: string;
      fim: string;
    };
    statistics?: {
      empresas: Array<{
        nome: string;
        totalClientes: number;
        valores: {
          multas: number;
          locacoes: number;
          diversos: number;
        };
        porStatus: {
          cancelado: number;
          congelado: number;
          ativo: number;
        };
      }>;
    };
  };
}

export interface ImportacaoLog {
  id: string;
  nomeArquivo: string;
  totalRegistros: number;
  sucessos: number;
  erros: number;
  detalhesErros?: string;
  tempoProcessamento: number;
  createdAt: string;
}

// Theme types
export interface ThemeConfig {
  mode: 'light' | 'dark' | 'system';
}

// Notification types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  createdAt: Date;
}

// Form types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'date' | 'select' | 'textarea';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

// Navigation types
export interface NavItem {
  name: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number;
  children?: NavItem[];
}

// Modal types
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
}

// Table types
export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (value: unknown, item: T) => React.ReactNode;
  className?: string;
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  emptyMessage?: string;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface KanbanEtapa {
  id: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  ordem: number;
  isAtivo: boolean;
  createdAt: Date;
  updatedAt: Date;
  _count?: {
    titulos: number;
  };
}

export interface CreateKanbanEtapaRequest {
  nome: string;
  descricao?: string;
  cor?: string;
  ordem: number;
}

export interface UpdateKanbanEtapaRequest {
  nome?: string;
  descricao?: string;
  cor?: string;
  ordem?: number;
  isAtivo?: boolean;
}

export interface KanbanEtapaMeta {
  id: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  ordem: number;
  isAtivo: boolean;
}

export interface Kanban {
  id: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  isAtivo: boolean;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  etapas?: KanbanEtapa[];
  _count?: {
    titulos: number;
    etapas: number;
  };
}

export interface CreateKanbanRequest {
  nome: string;
  descricao?: string;
  cor?: string;
  isDefault?: boolean;
}

export interface UpdateKanbanRequest {
  nome?: string;
  descricao?: string;
  cor?: string;
  isAtivo?: boolean;
  isDefault?: boolean;
}

export interface KanbanResponse {
  id: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  isAtivo: boolean;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  etapas?: KanbanEtapaResponse[];
  _count?: {
    titulos: number;
    etapas: number;
  };
}

export interface UserKanban {
  id: string;
  userId: string;
  kanbanId: string;
  etapasIds?: string | null; // JSON array de IDs das etapas
  createdAt: Date;
  updatedAt: Date;
  user?: User;
  kanban?: Kanban;
}

export interface CreateUserKanbanRequest {
  userId: string;
  kanbanId: string;
  etapasIds?: string[]; // Array de IDs das etapas que o usuário pode acessar
}

export interface UpdateUserKanbanRequest {
  etapasIds?: string[]; // Array de IDs das etapas que o usuário pode acessar
}

export interface UserKanbanResponse {
  id: string;
  userId: string;
  kanbanId: string;
  etapasIds?: string | null;
  createdAt: Date;
  updatedAt: Date;
  user?: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
  };
  kanban?: KanbanResponse;
}

export interface KanbanEtapa {
  id: string;
  kanbanId: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  ordem: number;
  isAtivo: boolean;
  createdAt: Date;
  updatedAt: Date;
  kanban?: Kanban;
}

export interface CreateKanbanEtapaRequest {
  kanbanId: string;
  nome: string;
  descricao?: string;
  cor?: string;
  ordem: number;
}

export interface UpdateKanbanEtapaRequest {
  nome?: string;
  descricao?: string;
  cor?: string;
  ordem?: number;
  isAtivo?: boolean;
}

export interface KanbanEtapaResponse {
  id: string;
  kanbanId: string;
  nome: string;
  descricao?: string | null;
  cor: string;
  ordem: number;
  isAtivo: boolean;
  createdAt: Date;
  updatedAt: Date;
  kanban?: KanbanResponse;
  _count?: {
    titulos: number;
  };
}

export interface KanbanBoardResponse {
  kanban: Record<string, KanbanCard[]>;
  etapas: KanbanEtapaMeta[];
}

export interface ClienteFornecedor {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  whatsapp?: string;
  email?: string;
  companyId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  company: {
    id: string;
    name: string;
  };
  titulosEmAberto: number;
  valorTotalEmAberto: number;
  titulosVencidos: number;
  valorTotalVencido: number;
  totalTitulos: number;
  operadorAtual?: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    feedbacks: number;
    titulos: number;
  };
}

export interface ClienteFornecedorWithDetails extends ClienteFornecedor {
  titulos: Titulo[];
  feedbacks: Feedback[];
  historicoContatos: HistoricoContatosPorOperador[];
}

export interface HistoricoContatosPorOperador {
  operador: {
    id: string;
    name: string;
    email: string;
  };
  contatos: Feedback[];
  totalContatos: number;
}

export interface KanbanCard {
  id: string;
  tipo: 'cliente';
  cliente: ClienteFornecedor;
  titulos: Titulo[];
  titulosEmAberto: number;
  valorTotalEmAberto: number;
  titulosVencidos: number;
  valorTotalVencido: number;
  totalTitulos: number;
}

// Mercado do Zeh (Marketplace)
export type MarketplaceProductType = 'PRODUCT' | 'SERVICE';

export interface MarketplaceCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageUrl?: string | null;
  isActive: boolean;
  sortOrder: number;
  companyId?: string | null;
  parentId?: string | null;
  createdAt: string;
  updatedAt: string;
  parent?: MarketplaceCategory | null;
  company?: { id: string; name: string } | null;
  children?: { id: string; name: string; slug: string }[];
}

export interface MarketplaceProductImage {
  id: string;
  productId: string;
  url: string;
  alt?: string | null;
  sortOrder: number;
}

export interface MarketplaceProduct {
  id: string;
  companyId: string;
  categoryId: string;
  name: string;
  slug: string;
  description?: string | null;
  productType: MarketplaceProductType;
  price: number;
  compareAtPrice?: number | null;
  isActive: boolean;
  stock?: number | null;
  sku?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  category?: { id: string; name: string; slug: string };
  company?: { id: string; name: string };
  images?: MarketplaceProductImage[];
}

export interface MarketplaceProductsResponse {
  items: MarketplaceProduct[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface MarketplaceOffer {
  id: string;
  productId: string;
  title: string;
  discountPercent?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: string;
    name: string;
    slug: string;
    companyId?: string;
    company?: { id: string; name: string };
  };
}
